/**
 * Iter138 — Super-Admin System-SMTP-Konfiguration
 *
 * Zentrale SMTP-Zugangsdaten für System-Mails (Login-Codes, Onboarding-Mails,
 * System-Notifications). Wird bevorzugt vor Company-SMTP verwendet.
 */
import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Mail, Send, Info } from "lucide-react";

export default function SmtpSystemConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true,
    smtp_from_email: "",
    smtp_from_name: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/system-smtp");
      setCfg(r.data);
      if (r.data.configured) {
        setForm((prev) => ({
          ...prev,
          smtp_host: r.data.smtp_host || "",
          smtp_port: r.data.smtp_port || 587,
          smtp_username: r.data.smtp_username || "",
          smtp_use_tls: !!r.data.smtp_use_tls,
          smtp_from_email: r.data.smtp_from_email || "",
          smtp_from_name: r.data.smtp_from_name || "",
        }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konfiguration konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.smtp_host || !form.smtp_username || !form.smtp_from_email) {
      toast.error("Bitte Host, Username und Absender-E-Mail ausfüllen.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Leeres Passwort → aktueller Wert bleibt (siehe Backend)
      if (!payload.smtp_password) delete payload.smtp_password;
      await apiClient.put("/admin/system-smtp", payload);
      toast.success("SMTP-Konfiguration gespeichert.");
      setForm((prev) => ({ ...prev, smtp_password: "" }));
      setTestResult(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await apiClient.post("/admin/system-smtp/test");
      setTestResult(r.data);
      if (r.data.ok) toast.success(r.data.detail);
      else toast.error(r.data.detail);
    } catch (e) {
      setTestResult({ ok: false, detail: e.response?.data?.detail || "Test fehlgeschlagen" });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-10 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade SMTP-Konfiguration …
      </Card>
    );
  }

  const commonProviders = [
    { name: "Gmail / Google Workspace", host: "smtp.gmail.com", port: 587 },
    { name: "Microsoft 365 / Outlook", host: "smtp.office365.com", port: 587 },
    { name: "SendGrid", host: "smtp.sendgrid.net", port: 587 },
    { name: "Mailgun", host: "smtp.mailgun.org", port: 587 },
    { name: "Infomaniak", host: "mail.infomaniak.com", port: 587 },
  ];

  return (
    <div className="space-y-4" data-testid="smtp-system-config">
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-slate-900">System-SMTP — E-Mail-Versand</h2>
          </div>
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
              cfg?.configured
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-slate-50 text-slate-500 border-slate-200"
            }`}
            data-testid="smtp-config-status"
          >
            {cfg?.configured ? "✓ Konfiguriert" : "Nicht konfiguriert"}
          </span>
        </div>

        {cfg?.configured && (
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs bg-slate-50 border border-slate-200 rounded p-3">
            <div>
              <span className="text-slate-500">Server: </span>
              <span className="font-mono text-slate-800">{cfg.smtp_host}:{cfg.smtp_port}</span>
            </div>
            <div>
              <span className="text-slate-500">TLS: </span>
              <span className="font-mono text-slate-800">{cfg.smtp_use_tls ? "ja" : "nein"}</span>
            </div>
            <div>
              <span className="text-slate-500">Login: </span>
              <span className="font-mono text-slate-800">{cfg.smtp_username}</span>
            </div>
            <div>
              <span className="text-slate-500">Passwort: </span>
              <span className="font-mono text-slate-800">{cfg.smtp_password_masked || "—"}</span>
            </div>
            <div className="md:col-span-2">
              <span className="text-slate-500">Absender: </span>
              <span className="font-mono text-slate-800">
                {cfg.smtp_from_name ? `${cfg.smtp_from_name} <${cfg.smtp_from_email}>` : cfg.smtp_from_email}
              </span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label htmlFor="smtp_host">SMTP-Server *</Label>
            <Input
              id="smtp_host"
              value={form.smtp_host}
              onChange={(e) => setForm({ ...form, smtp_host: e.target.value.trim() })}
              placeholder="smtp.gmail.com"
              data-testid="smtp-host"
            />
          </div>
          <div>
            <Label htmlFor="smtp_port">Port *</Label>
            <Input
              id="smtp_port"
              type="number"
              value={form.smtp_port}
              onChange={(e) => setForm({ ...form, smtp_port: parseInt(e.target.value, 10) || 587 })}
              placeholder="587"
              data-testid="smtp-port"
            />
          </div>
          <div>
            <Label htmlFor="smtp_username">Login-Username *</Label>
            <Input
              id="smtp_username"
              value={form.smtp_username}
              onChange={(e) => setForm({ ...form, smtp_username: e.target.value.trim() })}
              placeholder="noreply@ch-buchhaltung.ch"
              data-testid="smtp-username"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="smtp_password">Passwort {cfg?.configured && <span className="text-xs text-slate-500">(leer = unverändert)</span>}</Label>
            <Input
              id="smtp_password"
              type="password"
              value={form.smtp_password}
              onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
              placeholder={cfg?.configured ? "•••• (bleibt gespeichert)" : "App-Passwort oder API-Key"}
              data-testid="smtp-password"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="smtp_from_email">Absender-E-Mail *</Label>
            <Input
              id="smtp_from_email"
              type="email"
              value={form.smtp_from_email}
              onChange={(e) => setForm({ ...form, smtp_from_email: e.target.value.trim() })}
              placeholder="noreply@ch-buchhaltung.ch"
              data-testid="smtp-from-email"
            />
          </div>
          <div>
            <Label htmlFor="smtp_from_name">Absender-Name</Label>
            <Input
              id="smtp_from_name"
              value={form.smtp_from_name}
              onChange={(e) => setForm({ ...form, smtp_from_name: e.target.value })}
              placeholder="Buchhaltung CH"
              data-testid="smtp-from-name"
            />
          </div>
          <div className="md:col-span-3 flex items-center gap-2 pt-2">
            <Switch
              id="smtp_use_tls"
              checked={form.smtp_use_tls}
              onCheckedChange={(v) => setForm({ ...form, smtp_use_tls: v })}
              data-testid="smtp-use-tls"
            />
            <Label htmlFor="smtp_use_tls" className="text-sm cursor-pointer">
              STARTTLS aktivieren (empfohlen für Port 587; bei Port 465 wird SMTPS/SSL automatisch verwendet)
            </Label>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="smtp-save-btn">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Speichern
          </Button>
          <Button onClick={handleTest} disabled={testing || !cfg?.configured} variant="outline" data-testid="smtp-test-btn">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
            Test-Mail an mich senden
          </Button>
          {testResult && (
            <span
              className={`text-xs flex items-center gap-1.5 ${testResult.ok ? "text-emerald-700" : "text-rose-700"}`}
              data-testid="smtp-test-result"
            >
              {testResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
              {testResult.detail}
            </span>
          )}
        </div>
      </Card>

      <Card className="p-4 bg-indigo-50 border-indigo-200">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-sm text-indigo-900">
            <p className="font-semibold">Häufige Anbieter</p>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1.5 text-xs">
              {commonProviders.map((p) => (
                <div key={p.host} className="flex items-center gap-2 bg-white rounded px-2 py-1 border border-indigo-100">
                  <button
                    type="button"
                    className="text-indigo-600 hover:underline text-xs"
                    onClick={() => setForm((prev) => ({ ...prev, smtp_host: p.host, smtp_port: p.port }))}
                    data-testid={`smtp-preset-${p.host}`}
                  >
                    {p.name}
                  </button>
                  <code className="text-slate-500 text-[10px] ml-auto">{p.host}:{p.port}</code>
                </div>
              ))}
            </div>
            <p className="text-xs mt-2 text-indigo-800">
              Tipp: Für Gmail brauchst du ein <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">App-Passwort</a> (2FA muss aktiv sein).
              Für SendGrid/Mailgun nutze den API-Key als Passwort mit Username <code className="bg-white px-1 rounded">apikey</code>.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
